import React, { useRef, useState } from 'react';
import { motion } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { triggerAuthLogout } from '@/app/providers/authEvents';
import type { UserProfile } from '@/features/user/types/types';
import { TicketIcon, HeartIcon, SettingsIcon, LogoutIcon, CameraIcon } from './icons/ProfileIcons';
import { ShoppingCartIcon } from '@/components/icons';
import LogoutConfirmationModal from './LogoutConfirmationModal';

interface ProfileSidebarProps {
  user: UserProfile;
  profilePic: string | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  activeView: 'profile' | 'tickets' | 'favourites' | 'settings' | 'purchases';
  onViewChange: (view: 'profile' | 'tickets' | 'favourites' | 'settings' | 'purchases') => void;
  loading?: boolean;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({ user, profilePic, onFileChange, activeView, onViewChange, loading = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const triggerFileSelect = () => fileInputRef.current?.click();

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    
    try {
      // Clear auth store (this will call the logout API endpoint to clear cookies)
      await logout();
      
      // Trigger AuthProvider to update immediately
      triggerAuthLogout();
      
      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if logout fails, still navigate and clear state
      triggerAuthLogout();
      navigate('/');
    } finally {
      setIsLoggingOut(false);
      setIsLogoutModalOpen(false);
    }
  };

  // Check if user data is loaded (has valid name)
  const isUserDataLoaded = !loading && user.first_name && user.last_name;

  return (
    <div className="lg:col-span-1 space-y-4 sm:space-y-6 lg:space-y-8">
      <motion.div 
        className="bg-gray-900 border border-gray-800 rounded-3xl p-4 sm:p-6 lg:p-8 flex flex-col items-center text-center"
        initial={{ opacity: 0, y: 20, scale: 0.95, boxShadow: "0 0 0px rgba(0, 0, 0, 0)" }}
        animate={{ opacity: 1, y: 0, scale: 1, boxShadow: "0 0 0px rgba(0, 0, 0, 0)" }}
        whileHover={{ 
          y: -4,
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
          transition: { type: "spring", stiffness: 300, damping: 30 }
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
          mass: 0.8
        }}
      >
        {loading || !isUserDataLoaded ? (
          // Skeleton loading state for profile card
          <>
            <div className="relative w-32 h-32 sm:w-36 sm:h-36 lg:w-40 lg:h-40 mb-4 sm:mb-5 lg:mb-6">
              <div className="w-32 h-32 sm:w-36 sm:h-36 lg:w-40 lg:h-40 rounded-full bg-gray-700 animate-pulse" />
            </div>
            <div className="h-7 sm:h-8 lg:h-9 w-40 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-4 sm:h-5 w-24 bg-gray-700 rounded animate-pulse" />
          </>
        ) : (
          // Loaded state with actual user data
          <>
            <motion.div 
              className="relative w-32 h-32 sm:w-36 sm:h-36 lg:w-40 lg:h-40 mb-4 sm:mb-5 lg:mb-6"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                delay: 0.2, 
                type: "spring", 
                stiffness: 300, 
                damping: 20 
              }}
            >
              <motion.div 
                className="w-32 h-32 sm:w-36 sm:h-36 lg:w-40 lg:h-40 rounded-full bg-amber-400 p-1"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <div className="w-full h-full rounded-full overflow-hidden border-4 border-gray-700 shadow-lg">
                  {profilePic ? (
                    <motion.img 
                      src={profilePic} 
                      alt="Profile" 
                      className="w-full h-full object-cover rounded-full"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <motion.div 
                      className="w-full h-full bg-blue-900/50 flex items-center justify-center text-3xl sm:text-4xl lg:text-5xl font-serif text-blue-200 rounded-full"
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      {user.first_name[0]}{user.last_name[0]}
                    </motion.div>
                  )}
                </div>
              </motion.div>
              <motion.button 
                onClick={triggerFileSelect} 
                className="absolute bottom-1 right-1 bg-violet-600 hover:bg-violet-500 rounded-full p-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-violet-500"
                initial={{ scale: 0, rotate: -90, boxShadow: "0 0 0px rgba(139, 92, 246, 0)" }}
                animate={{ scale: 1, rotate: 0, boxShadow: "0 0 0px rgba(139, 92, 246, 0)" }}
                whileHover={{ 
                  scale: 1.1,
                  boxShadow: "0 0 20px rgba(139,92,246,0.5)",
                  rotate: 5
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ 
                  delay: 0.4, 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 20 
                }}
              >
                <CameraIcon />
              </motion.button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={onFileChange} 
                className="hidden" 
                accept="image/png, image/jpeg" 
              />
            </motion.div>
            <motion.h2 
              className="text-xl sm:text-2xl lg:text-3xl font-serif font-medium"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              {user.first_name} {user.last_name}
            </motion.h2>
            <motion.p 
              className="text-amber-400 font-semibold tracking-wider mt-1 text-sm sm:text-base"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
            >
              VIP Member
            </motion.p>
          </>
        )}
      </motion.div>

      <motion.div 
        className="bg-gray-900 border border-gray-800 rounded-3xl p-4 sm:p-5 lg:p-6 space-y-1"
        initial={{ opacity: 0, y: 20, scale: 0.95, boxShadow: "0 0 0px rgba(0, 0, 0, 0)" }}
        animate={{ opacity: 1, y: 0, scale: 1, boxShadow: "0 0 0px rgba(0, 0, 0, 0)" }}
        whileHover={{ 
          y: -2,
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
          transition: { type: "spring", stiffness: 300, damping: 30 }
        }}
        transition={{
          delay: 0.3,
          type: "spring",
          stiffness: 300,
          damping: 30,
          mass: 0.8
        }}
      >
        {[
          { key: 'tickets', icon: TicketIcon, label: 'My Tickets' },
          { key: 'purchases', icon: ShoppingCartIcon, label: 'Previous Purchases' },
          { key: 'favourites', icon: HeartIcon, label: 'Favourites' },
          { key: 'profile', icon: SettingsIcon, label: 'Account Settings' }
        ].map((item, index) => (
          <motion.button 
            key={item.key}
            onClick={() => onViewChange(item.key as 'profile' | 'tickets' | 'favourites' | 'settings' | 'purchases')}
            className={`group flex items-center gap-4 p-3 rounded-xl w-full text-left transition-colors duration-200 ${
              activeView === item.key 
                ? 'bg-gray-800 border-l-2 border-violet-500 font-semibold text-violet-400' 
                : 'hover:text-gray-200'
            }`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ 
              x: 5,
              backgroundColor: activeView === item.key ? 'rgb(31, 41, 55)' : 'rgb(17, 24, 39)',
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ 
              delay: 0.4 + index * 0.1, 
              duration: 0.4,
              type: "spring",
              stiffness: 300,
              damping: 20
            }}
          >
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {item.key === 'purchases' ? <item.icon className="w-5 h-5" /> : <item.icon />}
            </motion.div>
            {item.label}
          </motion.button>
        ))}
        <motion.button 
          onClick={handleLogoutClick}
          className="group flex items-center gap-4 p-3 rounded-xl text-red-400 hover:text-red-500 transition-colors duration-200 w-full text-left"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ 
            x: 5,
            color: 'rgb(239, 68, 68)',
            transition: { type: "spring", stiffness: 400, damping: 10 }
          }}
          whileTap={{ scale: 0.98 }}
          transition={{ 
            delay: 0.7, 
            duration: 0.4,
            type: "spring",
            stiffness: 300,
            damping: 20
          }}
        >
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <LogoutIcon />
          </motion.div>
          Log Out
        </motion.button>
      </motion.div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
};

export default ProfileSidebar;
